// Event logger. Always writes to console + a ring buffer +
// window.__imersivoAnalytics. Optionally pipes to PostHog when
// `VITE_POSTHOG_KEY` is configured at build time (no-op otherwise — keeps
// the dev / preview workflow free of external dependencies).

import posthog from 'posthog-js';

export type AnalyticsEventName =
  | 'session_started'
  | 'session_duration'
  | 'room_viewed'
  | 'variant_changed'
  | 'beforeafter_toggled'
  | 'hotspot_clicked'
  | 'vr_supported'
  | 'vr_unsupported'
  | 'vr_entered'
  | 'vr_exited'
  | 'cta_clicked'
  | 'modal_opened'
  | 'modal_closed'
  | 'audio_toggled'
  | 'gaze_sample'
  | 'share_link_copied'
  | 'whatsapp_share_opened'
  | 'generated_variant_attached'
  // Failure / funnel-drop events — surface production problems in the
  // dashboard instead of only the browser console (see src/app/logger.ts).
  | 'chat_failed'
  | 'generate_failed'
  | 'asset_load_failed';

export interface AnalyticsEvent {
  name: AnalyticsEventName;
  ts: number;
  payload?: Record<string, unknown>;
}

const BUFFER_LIMIT = 2000;

interface ImportMetaEnv {
  readonly VITE_POSTHOG_KEY?: string;
  readonly VITE_POSTHOG_HOST?: string;
}

function readEnv(): ImportMetaEnv {
  // import.meta.env is statically replaced by Vite at build time. Cast
  // through unknown so the typecheck doesn't require the global Vite types.
  return ((import.meta as unknown as { env?: ImportMetaEnv }).env) ?? {};
}

class AnalyticsImpl {
  private readonly buffer: AnalyticsEvent[] = [];
  private readonly startedAt = Date.now();
  private posthogReady = false;
  /** Names that fire too often to send over the wire as individual events. */
  private readonly highFrequencyEvents = new Set<AnalyticsEventName>(['gaze_sample']);

  constructor() {
    const env = readEnv();
    if (env.VITE_POSTHOG_KEY) {
      try {
        posthog.init(env.VITE_POSTHOG_KEY, {
          api_host: env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
          capture_pageview: true,
          autocapture: false,
          // Batch high-frequency gaze samples so we don't DOS PostHog.
          request_batching: true,
        });
        this.posthogReady = true;
      } catch (err) {
        console.warn('[analytics] posthog init failed', err);
      }
    }
    this.log('session_started', { ua: navigator.userAgent });
    window.addEventListener('beforeunload', () => {
      this.log('session_duration', { ms: Date.now() - this.startedAt });
    });
  }

  log(name: AnalyticsEventName, payload?: Record<string, unknown>): void {
    const evt: AnalyticsEvent = { name, ts: Date.now(), payload };
    this.buffer.push(evt);
    if (this.buffer.length > BUFFER_LIMIT) this.buffer.shift();
    if (!this.highFrequencyEvents.has(name)) {
      // eslint-disable-next-line no-console
      console.info(`[analytics] ${name}`, payload ?? '');
    }
    if (this.posthogReady) {
      try {
        posthog.capture(name, payload);
      } catch {
        // Swallow — analytics must never crash the app.
      }
    }
  }

  getAll(): readonly AnalyticsEvent[] {
    return this.buffer.slice();
  }

  /** Subset of buffer matching the given event name. */
  getByName(name: AnalyticsEventName): readonly AnalyticsEvent[] {
    return this.buffer.filter((e) => e.name === name);
  }

  clear(): void {
    this.buffer.length = 0;
  }
}

let instance: AnalyticsImpl | null = null;

export function getAnalytics(): AnalyticsImpl {
  if (!instance) {
    instance = new AnalyticsImpl();
    (window as unknown as { __imersivoAnalytics?: AnalyticsImpl }).__imersivoAnalytics = instance;
  }
  return instance;
}

/**
 * True if PostHog initialised successfully (i.e. VITE_POSTHOG_KEY was set
 * at build time and posthog.init() didn't throw). Used by the dashboard to
 * tailor the footer hint — no point telling the operator to "configure
 * PostHog" when it's already wired.
 */
export function isPostHogReady(): boolean {
  return Boolean(instance && (instance as unknown as { posthogReady: boolean }).posthogReady);
}

export type Analytics = AnalyticsImpl;
